import { useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { getThemeClassName } from 'lib/theme/themeUtils'
import TextButton from 'LuxKit/Button/TextButton'
import {
  CRUISE_ROUTES,
  CRUISE_TOP_OFFER_FIXED_POSITION_IDS,
  CRUISE_BANK_OFFERS_FILTERS,
  CRUISE_BANK_UNIFIED_FILTERS,
} from 'constants/cruise'
import useOfferList from 'hooks/Offers/useOfferList'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'
import { insertFixedPositionCruiseOfferIds } from 'lib/cruises/insertFixedPositionCruiseOfferIds'

const EMPTY_FIXED_POSITION_CRUISE_OFFER_IDS: Array<never> = []

const TopOffersContainer = styled(Group)<{ $hasSearchBarOverlap: boolean }>`
  ${({ $hasSearchBarOverlap }) => $hasSearchBarOverlap && `margin-top: ${rem(48)};`}
`

interface Props {
  hasSearchBarOverlap?: boolean
}

function CruisePageTopOffersCarousel({ hasSearchBarOverlap = false }: Props) {
  const intl = useIntl()
  const isInternationalMarket = useCruiseInternationalMarket()
  const offerListFilters = isInternationalMarket ? CRUISE_BANK_OFFERS_FILTERS : CRUISE_BANK_UNIFIED_FILTERS
  const fixedPositionOfferIds = isInternationalMarket ? EMPTY_FIXED_POSITION_CRUISE_OFFER_IDS : CRUISE_TOP_OFFER_FIXED_POSITION_IDS
  const rawOfferList = useOfferList(offerListFilters)

  const offerList = useMemo((): App.OfferList => ({
    ...rawOfferList,
    offerIds: insertFixedPositionCruiseOfferIds(rawOfferList.offerIds, fixedPositionOfferIds),
  }), [fixedPositionOfferIds, rawOfferList])

  const viewMoreLabel = intl.formatMessage({
    id: '5nqFJM6RzdBgBbhPiQln',
    defaultMessage: 'View More',
    description: 'Button text to view more top cruise offers in the cruise section',
  })

  if (!offerList.fetching && !offerList.offerIds.length) return null

  return <TopOffersContainer $hasSearchBarOverlap={hasSearchBarOverlap} gap={32} direction="vertical">
    <LayoutContainer>
      <Group gap={16} direction="horizontal" horizontalAlign="space-between">
        <Heading variant="heading2">
          <FormattedMessage
            id="rG6K-EkHT_xjnE-l5kNq"
            defaultMessage="Today’s top <i>cruise</i> offers"
            description="Section heading for top cruise offers carousel in the cruises section"
            values={{ i: (chunks) => <i>{chunks}</i> }}
          />
        </Heading>
        <TextButton kind="tertiary" to={`${CRUISE_ROUTES.SEARCH_PAGE}`}>{viewMoreLabel}</TextButton>
      </Group>
    </LayoutContainer>

    <OfferListCarousel
      tileStyle="card-default"
      tileSize="medium"
      overrideOfferList={offerList}
      className={getThemeClassName({ desktop: 'default' })}
    />
  </TopOffersContainer>
}

export default CruisePageTopOffersCarousel
